import { useEffect, useState } from 'react';
import { Compass, ExternalLink, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then || Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  const wk = Math.floor(d / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function CompassActivityWidget({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/admin/compass-sync-log`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setLogs(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const total = logs.length;
  const uniqueContacts = new Set(logs.map((l) => l.contact_id)).size;
  const lastWeek = logs.filter((l) => {
    const t = new Date(l.initiated_at).getTime();
    return Date.now() - t < 7 * 24 * 60 * 60 * 1000;
  }).length;
  const recent = expanded ? logs.slice(0, 20) : logs.slice(0, 5);

  return (
    <div
      className="bg-white rounded-xl border border-secondary/20 shadow-sm overflow-hidden"
      data-testid="compass-activity-widget"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary/5 transition-colors"
        data-testid="compass-widget-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
            <Compass className="w-5 h-5 text-secondary" />
          </div>
          <div className="text-left">
            <h3 className="font-editorial text-lg leading-none">Compass Activity</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {loading
                ? 'Loading recent handoffs…'
                : total === 0
                  ? 'No handoffs yet — send your first contact to Compass'
                  : `${lastWeek} this week · ${uniqueContacts} contact${uniqueContacts !== 1 ? 's' : ''} handed off · ${total} total`}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-secondary/10 divide-y divide-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-secondary" />
            </div>
          ) : recent.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Once you send a contact to Compass, their handoff will show up here.
            </div>
          ) : (
            recent.map((log) => (
              <div
                key={log.id}
                className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50/70"
                data-testid={`compass-log-${log.id}`}
              >
                <div className="w-2 h-2 rounded-full bg-secondary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {log.contact_email || log.contact_id}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    Sent by {log.initiated_by || 'admin'}
                    {log.compass_configured === false && (
                      <span className="ml-2 text-amber-600">· fallback URL</span>
                    )}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {relativeTime(log.initiated_at)}
                </span>
                {log.url && (
                  <a
                    href={log.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-secondary/70 flex-shrink-0"
                    title="Open Compass link"
                    data-testid={`compass-log-open-${log.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
